/**
 * FeedbackTab Component
 * 
 * Employee stakeholder feedback tab content.
 * Manages feedback requests and responses.
 */

import { useState, useCallback, useEffect } from 'react'
import StakeholderFeedbackSystem from '@/components/performance/feedback/StakeholderFeedbackSystem'
import FeedbackRequestDialog from '@/components/performance/feedback/FeedbackRequestDialog'
import CampaignSubjectStatusTile from './CampaignSubjectStatusTile'
import { feedbackService } from '@/services/api/feedbackService'
import type { FeedbackRequest, FeedbackStakeholder } from '@/services/api/feedbackService'
import toast from 'react-hot-toast'

interface FeedbackTabProps {
  userRole: 'employee' | 'manager'
  userId: string | null
  employeeId?: string
}

export function FeedbackTab({ userRole, userId, employeeId }: FeedbackTabProps) {
  const [feedbackRequests, setFeedbackRequests] = useState<FeedbackRequest[]>([])
  const [pendingStakeholderResponses, setPendingStakeholderResponses] = useState<FeedbackStakeholder[]>([])
  const [completedStakeholderResponses, setCompletedStakeholderResponses] = useState<FeedbackStakeholder[]>([])
  const [loadingFeedback, setLoadingFeedback] = useState(false)
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false)
  const [feedbackRequestType, setFeedbackRequestType] = useState<'MATRIX' | 'EMPLOYEE_REQUESTED' | 'UPWARD_360' | 'INFORMATION_CHECKIN'>('EMPLOYEE_REQUESTED')

  /**
   * Load feedback data from backend
   */
  const loadFeedbackData = useCallback(async (targetEmployeeId?: string) => {
    try {
      setLoadingFeedback(true)
      const [allRequests, pending, completed] = await Promise.all([
        feedbackService.listFeedbackRequests({ as_requester: true, as_subject: true }),
        feedbackService.getPendingResponses(targetEmployeeId),
        feedbackService.getCompletedResponses(targetEmployeeId)
      ])
      // For employee view, filter out manager-initiated requests (matrix, upward_360).
      // Employees should only see their own requested feedback, not feedback their manager requested about them.
      const requests = userRole === 'employee'
        ? allRequests.filter(r => r.request_type === 'employee_requested' || r.request_type === 'project')
        : allRequests
      if (import.meta.env.DEV) console.log('📊 Feedback data loaded:', {
        allRequests: allRequests.length,
        filteredRequests: requests.length,
        pending: pending.length,
        completed: completed.length,
      })
      setFeedbackRequests(requests)
      setPendingStakeholderResponses(pending)
      setCompletedStakeholderResponses(completed)
    } catch (error) {
      console.error('Failed to load feedback data:', error)
      toast.error('Failed to load feedback data. Please try again.')
    } finally {
      setLoadingFeedback(false)
    }
  }, [userRole])

  // Load feedback data on mount
  useEffect(() => {
    if (employeeId) {
      loadFeedbackData(employeeId)
    }
  }, [employeeId, loadFeedbackData])

  /**
   * Handle creating a feedback request
   */
  const handleCreateRequest = async (request: any) => {
    // Check if this is a full request submission (from UAT/PROJECT dialog)
    // or just a dialog trigger (from the "Request Feedback" button)
    if (request.stakeholders && request.custom_questions) {
      // Full request submission - make API call
      if (import.meta.env.DEV) console.log('📤 Creating feedback request via API:', request)
      try {
        const result = await feedbackService.createFeedbackRequest(request)
        if (import.meta.env.DEV) console.log('✅ Feedback request created:', result)
        // #27: the request dialog raises the "Request sent to {name}" toast.
        loadFeedbackData(employeeId)
        return result
      } catch (error: any) {
        console.error('❌ Failed to create feedback request:', error)
        // Handle validation errors (422) properly
        const detail = error.response?.data?.detail
        let errorMessage = 'Failed to send feedback request'
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map((e: any) => e.msg || e.message || String(e)).join(', ')
        } else if (typeof detail === 'string') {
          errorMessage = detail
        } else if (detail?.msg) {
          errorMessage = detail.msg
        }
        toast.error(errorMessage)
        throw error
      }
    } else {
      // Simple dialog trigger
      if (request.type === 'employee-requested') {
        setFeedbackRequestType('EMPLOYEE_REQUESTED')
      } else {
        setFeedbackRequestType('MATRIX')
      }
      setShowFeedbackDialog(true)
    }
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      {employeeId && <CampaignSubjectStatusTile userId={employeeId} />}
      <StakeholderFeedbackSystem
        userRole={userRole}
        requests={feedbackRequests}
        pendingStakeholderResponses={pendingStakeholderResponses}
        completedStakeholderResponses={completedStakeholderResponses}
        userId={userId || ''}
        loading={loadingFeedback}
        onCreateRequest={handleCreateRequest}
        onRefresh={() => loadFeedbackData(employeeId)}
      />
      
      {/* Feedback Request Dialog */}
      {showFeedbackDialog && (
        <FeedbackRequestDialog
          open={showFeedbackDialog}
          onOpenChange={setShowFeedbackDialog}
          requestType={feedbackRequestType}
          currentUserId={userId || ''}
          onSubmit={async (data) => {
            try {
              // Add required fields that may be missing from the dialog
              const enrichedData = {
                ...data,
                requested_by_user_id: userId || '',
                is_anonymous: data.is_confidential || false,  // Default to non-anonymous
                integrate_to_evidence: true,  // Default to integrating with evidence
                request_type: data.request_type.toLowerCase().replace('-', '_'),  // Ensure lowercase format
              }
              await feedbackService.createFeedbackRequest(enrichedData as any)
              // #27: toast is raised by the request dialog ("Request sent to {name}").
              setShowFeedbackDialog(false)
              loadFeedbackData(employeeId)
            } catch (error: any) {
              console.error('Failed to create feedback request:', error)
              const errorMessage = error.response?.data?.detail || 'Failed to send feedback request'
              toast.error(errorMessage)
            }
          }}
        />
      )}
    </div>
  )
}

export default FeedbackTab
